import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLogs } from "../../store/slices/activityLogSlice";
import {
  Activity,
  Search,
  TrendingUp,
  Clock,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

const ActivityLogs = () => {
  const dispatch = useDispatch();
  const { logs, loading, error } = useSelector((state) => state.activityLogs);
  // console.log("logs", logs);
  const [filters, setFilters] = useState({
    date: "today",
    user: "all",
    action: "all",
    search: "",
  });

  // Load activity logs when component mounts
  useEffect(() => {
    dispatch(getActivityLogs());
  }, [dispatch]);

  // Filter logs based on current filters
  const filteredLogs =
    logs?.filter((log) => {
      if (filters.date !== "all") {
        // Add date filtering logic here
      }

      // User filter
      if (filters.user !== "all" && log.user_role !== filters.user) {
        return false;
      }

      // Action filter
      if (filters.action !== "all" && !log.action.includes(filters.action)) {
        return false;
      }

      if (
        filters.search &&
        !log.description.toLowerCase().includes(filters.search.toLowerCase()) &&
        !log.user_name.toLowerCase().includes(filters.search.toLowerCase())
      ) {
        return false;
      }

      return true;
    }) || [];

  // Calculate statistics from the data
  const calculateStats = () => {
    if (!logs) return null;

    const today = new Date().toDateString();
    const todayLogs = logs.filter(
      (log) => new Date(log.created_at).toDateString() === today
    );

    const userActivity = {};
    logs.forEach((log) => {
      userActivity[log.user_name] = (userActivity[log.user_name] || 0) + 1;
    });

    const mostActiveUser = Object.keys(userActivity).reduce(
      (a, b) => (userActivity[a] > userActivity[b] ? a : b),
      ""
    );

    // Calculate hourly activity
    const hourlyActivity = {};
    logs.forEach((log) => {
      const hour = new Date(log.created_at).getHours();
      hourlyActivity[hour] = (hourlyActivity[hour] || 0) + 1;
    });

    const peakHour = Object.keys(hourlyActivity).reduce(
      (a, b) => (hourlyActivity[a] > hourlyActivity[b] ? a : b),
      ""
    );
    const peakActivity = `${peakHour}:00 - ${parseInt(peakHour) + 1}:00`;

    return {
      totalLogs: logs.length,
      today: todayLogs.length,
      thisWeek: logs.filter((log) => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return new Date(log.created_at) >= oneWeekAgo;
      }).length,
      mostActiveUser,
      mostActiveCount: userActivity[mostActiveUser] || 0,
      peakActivity,
    };
  };

  const logStats = calculateStats() || {
    totalLogs: 0,
    today: 0,
    thisWeek: 0,
    mostActiveUser: "N/A",
    mostActiveCount: 0,
    peakActivity: "N/A",
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get action icon and color based on action type
  const getActionInfo = (action) => {
    const actionMap = {
      login: {
        icon: "🔐",
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "User Login",
      },
      register: {
        icon: "👤",
        color: "bg-green-100 text-green-800 border-green-200",
        label: "User Registration",
      },
      patient_registered: {
        icon: "📝",
        color: "bg-purple-100 text-purple-800 border-purple-200",
        label: "Patient Registered",
      },
      test_ordered: {
        icon: "🧪",
        color: "bg-orange-100 text-orange-800 border-orange-200",
        label: "Test Ordered",
      },
      result_entered: {
        icon: "📊",
        color: "bg-teal-100 text-teal-800 border-teal-200",
        label: "Result Entered",
      },
      default: {
        icon: "📋",
        color: "bg-gray-100 text-gray-800 border-gray-200",
        label: action,
      },
    };

    return actionMap[action] || actionMap.default;
  };

  // Get user role color
  const getUserRoleColor = (role) => {
    const roleColors = {
      admin: "bg-red-100 text-red-800 border-red-200",
      receptionist: "bg-blue-100 text-blue-800 border-blue-200",
      laboratorist: "bg-green-100 text-green-800 border-green-200",
      patient: "bg-purple-100 text-purple-800 border-purple-200",
      default: "bg-gray-100 text-gray-800 border-gray-200",
    };

    return roleColors[role] || roleColors.default;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative w-20 h-20 mx-auto">
            <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-[#235F72] rounded-full border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-600 font-medium">Loading activity logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-[#235F72] to-[#1a4a5a] bg-clip-text text-transparent">
              System Activity Logs
            </h1>
            <p className="text-gray-600 mt-1">Monitor system activities and user actions</p>
          </div>
          <button
            onClick={() => dispatch(getActivityLogs())}
            className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <RefreshCw className="w-4 h-4 text-[#235F72]" />
            <span className="text-sm font-medium text-[#235F72]">Refresh</span>
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-white border border-red-200 rounded-xl p-4 flex items-center space-x-3 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span className="text-sm">{String(error)}</span>
          </div>
        )}

        {/* Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { label: "Total Logs", value: logStats.totalLogs, icon: Activity, color: "from-blue-500 to-blue-600" },
            { label: "Today", value: logStats.today, icon: Clock, color: "from-emerald-500 to-emerald-600" },
            { label: "This Week", value: logStats.thisWeek, icon: TrendingUp, color: "from-purple-500 to-purple-600" },
          ].map((card, idx) => {
            const Icon = card.icon;
            return (
              <div key={idx} className="relative group bg-white rounded-2xl p-6 border border-gray-200 hover:border-transparent hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                <div className="relative z-10 flex items-center justify-between">
                  <div>
                    <div className="text-3xl font-bold text-gray-900">{card.value}</div>
                    <div className="text-sm font-semibold text-gray-700">{card.label}</div>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filter Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Range
            </label>
            <select
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="all">All Time</option>
            </select>
          </div> */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Role
            </label>
            <select
              value={filters.user}
              onChange={(e) => setFilters({ ...filters, user: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent"
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="receptionist">Receptionist</option>
              <option value="laboratorist">Laboratorist</option>
              <option value="patient">Patient</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action Type
            </label>
            <select
              value={filters.action}
              onChange={(e) =>
                setFilters({ ...filters, action: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent"
            >
              <option value="all">All Actions</option>
              <option value="login">User Login</option>
              <option value="register">User Registration</option>
              <option value="patient">Patient Registration</option>
              <option value="test">Test Order</option>
              <option value="result">Result Entry</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search users, actions, descriptions..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#235F72] focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>
          </div>

          <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-500">
            Showing {filteredLogs.length} of {logs?.length || 0} logs
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() =>
                setFilters({
                  date: "all",
                  user: "all",
                  action: "all",
                  search: "",
                })
              }
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200"
            >
              Clear Filters
            </button>
            <button
              onClick={() => dispatch(getActivityLogs())}
              className="px-6 py-2 rounded-lg transition duration-200 font-semibold bg-[#235F72] text-white hover:bg-[#1a4a5a]"
            >
              Refresh Data
            </button>
          </div>
          </div>
        </div>

        {/* Activity Logs Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <th className="text-left py-4 px-6 text-[#235F72] font-semibold">
                  Date & Time
                </th>
                <th className="text-left py-4 px-6 text-[#235F72] font-semibold">
                  User
                </th>
                <th className="text-left py-4 px-6 text-[#235F72] font-semibold">
                  Action
                </th>
                <th className="text-left py-4 px-6 text-[#235F72] font-semibold">
                  Description
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => {
                  const actionInfo = getActionInfo(log.action);
                  return (
                    <tr
                      key={log.id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="py-4 px-6">
                        <div className="font-medium text-[#235F72] text-sm">
                          {formatDate(log.created_at)}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="space-y-1">
                          <div className="font-medium text-gray-900">
                            {log.user_name}
                          </div>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getUserRoleColor(
                              log.user_role
                            )}`}
                          >
                            {log.user_role}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${actionInfo.color}`}
                        >
                          <span className="mr-2">{actionInfo.icon}</span>
                          {actionInfo.label}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="text-gray-700 max-w-md">
                          {log.description}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="4"
                      className="py-12 px-6 text-center text-gray-500"
                  >
                    No activity logs found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
          {/* Footer indicator */}
          {filteredLogs.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Showing latest {filteredLogs.length} activities
              </span>
            </div>
          )}
        </div>

        {/* Log Statistics */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Activity Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <span className="font-medium text-[#085DB6]">Most Active User:</span> {logStats.mostActiveUser} ({logStats.mostActiveCount} actions)
            </div>
            <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
              <span className="font-medium text-[#235F72]">Peak Activity:</span> {logStats.peakActivity}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
